import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [location, setLocation] = useState(() => {
    // Load from localStorage on init
    const saved = localStorage.getItem('marketplace_location');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved location:', e);
      }
    }
    return {
      zip: '',
      radius: 25, // Default 25 miles
      city: '',
      state: '',
      isSet: false
    };
  });

  // Save to localStorage whenever location changes
  useEffect(() => {
    if (location.isSet) {
      localStorage.setItem('marketplace_location', JSON.stringify(location));
    }
  }, [location]);

  const setLocationData = useCallback((locationData) => {
    setLocation({
      ...locationData,
      isSet: true
    });
  }, []);

  const clearLocation = useCallback(() => {
    setLocation({
      zip: '',
      radius: 25,
      city: '',
      state: '',
      isSet: false
    });
    localStorage.removeItem('marketplace_location');
  }, []);

  const updateRadius = useCallback((radius) => {
    setLocation(prev => ({
      ...prev,
      radius: parseInt(radius)
    }));
  }, []);

  const value = useMemo(() => ({
    location,
    setLocationData,
    clearLocation,
    updateRadius,
    isLocationSet: location.isSet && location.zip
  }), [location, setLocationData, clearLocation, updateRadius]);

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};